import React, { useEffect, useMemo, useState } from 'react'
import { FaChevronRight, FaFileCsv, FaSyncAlt } from 'react-icons/fa'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import api from '../../../helpers/apiRequester'
import { fetchApprovedLenders } from '../../../helpers/approvedLenders'
import { LenderCategoryStats, categoryLabel } from '../../reusable/lenderCategoryStats'
import styles from './styles.module.scss'

/**
 * Admin > Approved Lenders
 *
 * The single place the approved lender list is managed. Every row is a
 * funder-monitoring `funder_defendants` record; the Active switch controls
 * whether the lender is approved everywhere (FairClaim / FFJ API gates,
 * funding batch collection, SRDS imports, eligibility stats, scrape-time
 * matching, dashboard pickers and the acceptance-criteria documents).
 */

interface FundedFootprint {
    claims: number
    clients: number
    batches: number
    value: number
}

interface AdminLender {
    id: number
    defendant: string
    aliases: string[]
    category: number | string | null
    active: boolean
    updatedAt: string | null
    updatedBy: string | null
    funded: FundedFootprint
}

interface AdminListResponse {
    success: boolean
    lenders: AdminLender[]
    counts: { total: number; active: number; inactive: number }
}

type StatusFilter = '' | 'active' | 'inactive'
type LenderSort = 'category_asc' | 'name_asc' | 'name_desc' | 'claims_desc'

const SORTS: { value: LenderSort; label: string }[] = [
    { value: 'category_asc', label: 'Category, then A–Z' },
    { value: 'name_asc', label: 'Lender A–Z' },
    { value: 'name_desc', label: 'Lender Z–A' },
    { value: 'claims_desc', label: 'Most funded claims' },
]

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })
const int = new Intl.NumberFormat('en-GB')

const formatDateTime = (value: string | null) => {
    if (!value) return null
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const csvCell = (value: string | number | null | undefined): string => {
    if (value == null) return ''
    const text = String(value)
    return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

const categoryKey = (category: AdminLender['category']) => (category == null ? '' : String(category))

// Category 1 first, then 2, with uncategorised lenders at the end.
const compareCategory = (a: AdminLender['category'], b: AdminLender['category']) => {
    const ka = categoryKey(a)
    const kb = categoryKey(b)
    if (ka === kb) return 0
    if (ka === '') return 1
    if (kb === '') return -1
    return ka.localeCompare(kb, undefined, { numeric: true })
}

export const ApprovedLendersView = () => {
    const [lenders, setLenders] = useState<AdminLender[]>([])
    const [counts, setCounts] = useState<AdminListResponse['counts']>({ total: 0, active: 0, inactive: 0 })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [filter, setFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('')
    const [categoryFilter, setCategoryFilter] = useState('')
    const [sort, setSort] = useState<LenderSort>('category_asc')
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [pendingId, setPendingId] = useState<number | null>(null)
    const [statsRefreshKey, setStatsRefreshKey] = useState(0)

    const applyResponse = (payload: AdminListResponse) => {
        setLenders(payload.lenders || [])
        setCounts(payload.counts || { total: 0, active: 0, inactive: 0 })
    }

    const load = async (silent = false) => {
        setLoading(true)
        setError(null)
        if (!silent) Loading.circle('Loading approved lenders...')
        try {
            const response = await api({ url: '/approved-lenders-admin', data: {} })
            if (response.data?.success) applyResponse(response.data)
            else setError(response.data?.message || 'Could not load the approved lender list.')
        } catch (err: any) {
            console.error('Error loading approved lenders:', err)
            setError(err?.response?.data?.message || err?.message || 'Failed to load the approved lender list.')
        } finally {
            setLoading(false)
            if (!silent) Loading.remove()
        }
    }

    useEffect(() => {
        void load()
    }, [])

    const categories = useMemo(() => {
        const set = new Set<string>()
        for (const lender of lenders) set.add(categoryKey(lender.category))
        return Array.from(set).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b, undefined, { numeric: true })))
    }, [lenders])

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase()
        let rows = lenders.filter((lender) => {
            if (statusFilter === 'active' && !lender.active) return false
            if (statusFilter === 'inactive' && lender.active) return false
            if (categoryFilter && categoryKey(lender.category) !== categoryFilter) return false
            if (!q) return true
            return lender.defendant.toLowerCase().includes(q) || lender.aliases.some((alias) => alias.toLowerCase().includes(q))
        })

        const byName = (a: AdminLender, b: AdminLender) =>
            a.defendant.localeCompare(b.defendant, 'en-GB', { sensitivity: 'base' })
        const comparators: Record<LenderSort, (a: AdminLender, b: AdminLender) => number> = {
            name_asc: byName,
            name_desc: (a, b) => byName(b, a),
            claims_desc: (a, b) => b.funded.claims - a.funded.claims || byName(a, b),
            category_asc: (a, b) => compareCategory(a.category, b.category) || byName(a, b),
        }
        rows = [...rows].sort(comparators[sort])
        return rows
    }, [lenders, filter, statusFilter, categoryFilter, sort])

    const handleToggle = (lender: AdminLender) => {
        const turningOff = lender.active
        const footprint = lender.funded.claims
            ? `${int.format(lender.funded.claims)} funded claims across ${int.format(lender.funded.batches)} batches stay funded and keep resolving to this lender.`
            : 'No funded claims currently reference this lender.'
        Confirm.show(
            turningOff ? 'Switch lender off' : 'Switch lender on',
            turningOff
                ? `${lender.defendant} will be rejected by the FairClaim / FFJ APIs, excluded from funding batches, SRDS imports and eligibility counts, and removed from every lender list and document. ${footprint}`
                : `${lender.defendant} will be approved again everywhere: API gates, funding batches, SRDS imports, eligibility counts, lender lists and documents.`,
            turningOff ? 'Switch off' : 'Switch on',
            'Cancel',
            async () => {
                try {
                    setPendingId(lender.id)
                    Loading.circle(turningOff ? 'Switching lender off...' : 'Switching lender on...')
                    const response = await api({
                        url: '/approved-lenders-update',
                        data: { id: lender.id, active: !lender.active },
                    })
                    if (response.data?.success) {
                        applyResponse(response.data)
                        setStatsRefreshKey((key) => key + 1)
                        // Drop the page-level memo so pickers pick up the change.
                        await fetchApprovedLenders({ force: true }).catch(() => [])
                    } else {
                        Report.failure('Update failed', response.data?.message || 'Could not update the lender.', 'Okay')
                    }
                } catch (err: any) {
                    Report.failure('Update failed', err?.response?.data?.message || 'Could not update the lender.', 'Okay')
                } finally {
                    setPendingId(null)
                    Loading.remove()
                }
            },
            undefined,
            { messageMaxLength: 2000 },
        )
    }

    const exportCsv = () => {
        const header = ['Lender', 'Status', 'Category', 'Aliases', 'Funded claims', 'Funded clients', 'Batches', 'Funded value', 'Last updated', 'Updated by']
        const rows = filtered.map((lender) => [
            lender.defendant,
            lender.active ? 'Active' : 'Inactive',
            categoryLabel(lender.category),
            lender.aliases.join(' | '),
            lender.funded.claims,
            lender.funded.clients,
            lender.funded.batches,
            lender.funded.value.toFixed(2),
            lender.updatedAt ?? '',
            lender.updatedBy ?? '',
        ])
        const csv = [header, ...rows].map((cells) => cells.map(csvCell).join(',')).join('\r\n')
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `approved-lenders-${new Date().toISOString().slice(0, 10)}.csv`
        link.click()
        URL.revokeObjectURL(url)
    }

    // Inline expansion for a lender row: funded footprint, last change and the
    // alias list, so the table itself can stay narrow.
    const renderLenderDetail = (lender: AdminLender) => {
        const stats: { label: string; value: string }[] = [
            { label: 'Funded claims', value: int.format(lender.funded.claims) },
            { label: 'Funded clients', value: int.format(lender.funded.clients) },
            { label: 'Batches', value: int.format(lender.funded.batches) },
            { label: 'Funded value', value: gbp.format(lender.funded.value) },
            { label: 'Category', value: categoryLabel(lender.category) },
            {
                label: 'Last change',
                value: formatDateTime(lender.updatedAt)
                    ? `${formatDateTime(lender.updatedAt)}${lender.updatedBy ? ` · ${lender.updatedBy}` : ''}`
                    : '—',
            },
        ]
        return (
            <div className={styles.miniView}>
                <div className={styles.miniHeader}>
                    <h4 className={styles.miniTitle}>{lender.defendant}</h4>
                    <span className={styles.miniMeta}>
                        {lender.aliases.length
                            ? `${lender.aliases.length} known name${lender.aliases.length === 1 ? '' : 's'} resolve to this lender`
                            : 'Only the canonical name resolves to this lender'}
                    </span>
                </div>
                <div className={styles.miniStats}>
                    {stats.map((stat) => (
                        <div key={stat.label} className={styles.miniStat}>
                            <span className={styles.miniStatValue}>{stat.value}</span>
                            <span className={styles.miniStatLabel}>{stat.label}</span>
                        </div>
                    ))}
                </div>
                {lender.aliases.length > 0 && (
                    <>
                        <p className={styles.aliasHeading}>Aliases</p>
                        <div className={styles.aliasList}>
                            {lender.aliases.map((alias) => (
                                <span key={alias} className={styles.aliasChip}>
                                    {alias}
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </div>
        )
    }

    return (
        <div className={styles.wrap}>
            <p className={styles.intro}>
                The approved lender list controls which lenders Sentinel will pursue. Switching a lender off removes it
                from the FairClaim and FFJ API gates, funding batch collection, SRDS imports, eligibility counts, every
                lender picker in the dashboard and the published acceptance-criteria documents. Claims already funded
                keep resolving to the lender, so historical batches are unaffected.
            </p>

            {error && <div className={styles.errorBanner}>{error}</div>}

            <div className={styles.summaryRow}>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{int.format(counts.active)}</span>
                    <span className={styles.summaryLabel}>Active lenders</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={`${styles.summaryValue} ${counts.inactive ? styles.summaryValueInactive : ''}`}>
                        {int.format(counts.inactive)}
                    </span>
                    <span className={styles.summaryLabel}>Switched off</span>
                </div>
                <div className={styles.summaryCard}>
                    <span className={styles.summaryValue}>{int.format(counts.total)}</span>
                    <span className={styles.summaryLabel}>Defendants on file</span>
                </div>
            </div>

            <div className={styles.categoryPanel}>
                <LenderCategoryStats refreshKey={statsRefreshKey} />
            </div>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    type="search"
                    placeholder="Filter by lender or alias…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                <select
                    className={styles.toolbarSelect}
                    value={sort}
                    onChange={(e) => setSort(e.target.value as LenderSort)}
                    aria-label="Sort lenders"
                >
                    {SORTS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <select
                    className={styles.toolbarSelect}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    aria-label="Filter by status"
                >
                    <option value="">Any status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Switched off</option>
                </select>
                <select
                    className={styles.toolbarSelect}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    aria-label="Filter by category"
                >
                    <option value="">Any category</option>
                    {categories.map((category) => (
                        <option key={category || 'null'} value={category}>
                            {categoryLabel(category === '' ? null : category)}
                        </option>
                    ))}
                </select>
                <button type="button" className={styles.refreshBtn} onClick={() => load()} disabled={loading}>
                    <FaSyncAlt /> Refresh
                </button>
                <button
                    type="button"
                    className={styles.exportBtn}
                    onClick={exportCsv}
                    disabled={loading || filtered.length === 0}
                    title="Export the lenders currently shown"
                >
                    <FaFileCsv /> Export CSV
                </button>
            </div>

            <p className={styles.resultLine}>
                Showing {int.format(filtered.length)} of {int.format(lenders.length)} lenders
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Lender</th>
                            <th>Category</th>
                            <th>Funded</th>
                            <th>Status</th>
                            <th aria-label="Active switch" />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={5}>
                                    <div className={styles.emptyState}>
                                        {loading
                                            ? 'Loading…'
                                            : lenders.length === 0
                                              ? 'No defendants found in funder_defendants.'
                                              : 'No lenders match this filter.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filtered.map((lender, index) => {
                                const isOpen = selectedId === lender.id
                                // Section rows only when grouped by category, so the
                                // default view reads as Category 1 / Category 2 blocks.
                                const showGroupRow =
                                    sort === 'category_asc' &&
                                    (index === 0 || compareCategory(filtered[index - 1].category, lender.category) !== 0)
                                return (
                                    <React.Fragment key={lender.id}>
                                        {showGroupRow && (
                                            <tr className={styles.groupRow}>
                                                <td colSpan={5}>
                                                    {categoryLabel(lender.category)}
                                                    <span className={styles.groupCount}>
                                                        {int.format(
                                                            filtered.filter(
                                                                (row) => compareCategory(row.category, lender.category) === 0,
                                                            ).length,
                                                        )}
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                        <tr
                                            className={`${isOpen ? styles.selected : ''} ${lender.active ? '' : styles.inactiveRow}`}
                                            onClick={() => setSelectedId((current) => (current === lender.id ? null : lender.id))}
                                        >
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>
                                                        <FaChevronRight
                                                            className={`${styles.rowChevron} ${isOpen ? styles.rowChevronOpen : ''}`}
                                                        />
                                                        {lender.defendant}
                                                    </span>
                                                    <span className={styles.stackSecondary}>
                                                        {lender.aliases.length
                                                            ? `${lender.aliases.length} alias${lender.aliases.length === 1 ? '' : 'es'}`
                                                            : 'No aliases'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={`${styles.categoryPill} ${lender.category == null ? styles.uncategorised : ''}`}
                                                >
                                                    {categoryLabel(lender.category)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>
                                                        {int.format(lender.funded.claims)} claims
                                                    </span>
                                                    <span className={styles.stackSecondary}>
                                                        {int.format(lender.funded.clients)} clients
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={`${styles.statusPill} ${lender.active ? styles.active : styles.inactive}`}
                                                >
                                                    {lender.active ? 'Active' : 'Switched off'}
                                                </span>
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <label className={styles.switch} title={lender.active ? 'Switch off' : 'Switch on'}>
                                                    <input
                                                        type="checkbox"
                                                        checked={lender.active}
                                                        disabled={pendingId !== null}
                                                        onChange={() => handleToggle(lender)}
                                                    />
                                                    <span className={styles.slider} />
                                                </label>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan={5}>{renderLenderDetail(lender)}</td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
